import type { Editor } from "@tiptap/react";
import type { Asset } from "schemas/generated/asset";
import type {
  ChatAssetItem,
  ChatProductItem,
} from "schemas/generated/chatMessage";
import type { Product } from "schemas/generated/product";

import { serializeChatTokenPayloadToComponent } from "./chatTokenSerialization";

function createAssetChatItem(asset: Asset): ChatAssetItem {
  return {
    id: asset.id,
    name: asset.name,
    url: asset.url,
    fileExtension: asset.fileExtension,
  };
}

function createProductChatItem(product: Product): ChatProductItem {
  return {
    id: product.id,
    title: product.title,
    description: product.description ?? "",
    status: product.status,
    featuredMedia: product.featuredMedia,
    media: product.media,
    options: product.options,
    variants: product.variants,
    sellingPlanGroups: product.sellingPlanGroups,
  };
}

function insertContextItemInEditor(
  editor: Editor,
  type: "asset" | "product",
  data: ChatAssetItem | ChatProductItem,
  atPosition?: number | null,
) {
  const htmlFragment = serializeChatTokenPayloadToComponent(type, data);
  if (!htmlFragment) {
    return;
  }

  /*
   * NOTE (Kevin 10-02-2025): Trailing space prevents the token from merging
   * with adjacent text when inserted into the editor.
   */
  const html = `${htmlFragment} `;

  if (atPosition !== null && atPosition !== undefined) {
    // Replace the @ with the component at the tracked position
    editor
      .chain()
      .focus()
      .deleteRange({ from: atPosition, to: atPosition + 1 }) // Delete the @
      .insertContentAt(atPosition, html) // Insert component at @ position
      .run();
  } else {
    // Fallback: just insert the component at cursor position
    editor.chain().focus().insertContent(html).run();
  }
}

function findAtPositionInEditor(editor: Editor): number | null {
  const { state } = editor;
  const { selection } = state;
  const cursorPos = selection.anchor;

  // Find the @ character before the cursor
  for (let i = cursorPos - 1; i >= 0; i--) {
    const char = state.doc.textBetween(i, i + 1);
    if (char === "@") {
      return i;
    }
    // Stop searching if we hit a space or start of document
    if (char === " " || i === 0) {
      break;
    }
  }

  return null;
}

function selectContextItem(
  type: "asset" | "product",
  data: ChatAssetItem | ChatProductItem,
  editor: Editor | null,
) {
  if (!editor) {
    return;
  }

  const atPosition = findAtPositionInEditor(editor);
  insertContextItemInEditor(editor, type, data, atPosition);
}

export function selectAsset(asset: Asset, editor: Editor | null) {
  const chatAssetItemData = createAssetChatItem(asset);
  selectContextItem("asset", chatAssetItemData, editor);
}

export function selectProduct(product: Product, editor: Editor | null) {
  const productItemData = createProductChatItem(product);
  selectContextItem("product", productItemData, editor);
}
